/**
 * PTEL Tool - RegisterForm
 * Formulario de registro con nombre/apellidos separados e indicador de fortaleza
 *
 * @module components/auth/RegisterForm
 * @version 1.4.0
 * @changes
 *   1.4.0 — Elimina texto «Atérminos de uso» (no existe página pública).
 */

import type { FormEvent, CSSProperties } from 'react';
import { useState, useCallback, useRef, useEffect } from 'react';
import { Mail, Lock, Eye, EyeOff, AlertCircle, Loader2, User, CheckCircle2 } from 'lucide-react';

import type { RegisterFormProps, RegisterFormData, FormErrors, TouchedFields } from './auth.types';
import { VALIDATION_RULES } from './auth.types';
import {
  getAuthTokens, getButtonStyles, getCardStyles, getAlertStyles,
  calculatePasswordStrength, getStrengthColor, getStrengthLabel,
} from './auth.tokens';
import { useIsMounted } from '@/hooks/useIsMounted';

type RegisterField = keyof RegisterFormData;

function validateFirstName(n: string) {
  const t = n.trim();
  if (!t) return VALIDATION_RULES.firstName.required;
  if (t.length < VALIDATION_RULES.firstName.minLength) return VALIDATION_RULES.firstName.tooShort;
  if (t.length > VALIDATION_RULES.firstName.maxLength) return VALIDATION_RULES.firstName.tooLong;
}
function validateLastName(n: string) {
  const t = n.trim();
  if (!t) return VALIDATION_RULES.lastName.required;
  if (t.length < VALIDATION_RULES.lastName.minLength) return VALIDATION_RULES.lastName.tooShort;
  if (t.length > VALIDATION_RULES.lastName.maxLength) return VALIDATION_RULES.lastName.tooLong;
}
function validateEmail(email: string) {
  if (!email.trim()) return VALIDATION_RULES.email.required;
  if (!VALIDATION_RULES.email.pattern.test(email)) return VALIDATION_RULES.email.invalid;
}
function validatePassword(p: string) {
  if (!p) return VALIDATION_RULES.password.required;
  if (p.length < VALIDATION_RULES.password.minLength) return VALIDATION_RULES.password.tooShort;
  if (p.length > VALIDATION_RULES.password.maxLength) return VALIDATION_RULES.password.tooLong;
  if (!/[A-Z]/.test(p) || !/[a-z]/.test(p) || !/\d/.test(p)) return VALIDATION_RULES.password.weak;
}
function validateConfirm(p: string, c: string) {
  if (!c) return VALIDATION_RULES.confirmPassword.required;
  if (p !== c) return VALIDATION_RULES.confirmPassword.mismatch;
}

export function RegisterForm({
  theme,
  onSubmit,
  onNavigateToLogin,
  isLoading: externalLoading = false,
  externalError,
  onClearError,
  onClose,
  hideTitle = false,
  hideCard = false,
}: RegisterFormProps & { hideTitle?: boolean; hideCard?: boolean }) {
  const t = getAuthTokens(theme);
  const buttonStyles = getButtonStyles(theme);
  const cardStyles = getCardStyles(theme);
  const alertStyles = getAlertStyles(theme, 'error');

  const isMounted = useIsMounted();

  const [form, setForm] = useState<RegisterFormData>({
    firstName: '', lastName: '', email: '', password: '', confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pwCountdown, setPwCountdown] = useState<number | null>(null);
  const [cfCountdown, setCfCountdown] = useState<number | null>(null);
  const pwTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const cfTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => () => {
    if (pwTimerRef.current) clearInterval(pwTimerRef.current);
    if (cfTimerRef.current) clearInterval(cfTimerRef.current);
  }, []);

  const togglePassword = useCallback(() => {
    if (showPassword) {
      if (pwTimerRef.current) clearInterval(pwTimerRef.current);
      setShowPassword(false); setPwCountdown(null);
    } else {
      setShowPassword(true); setPwCountdown(5);
      pwTimerRef.current = setInterval(() => {
        setPwCountdown(prev => {
          if (prev === null || prev <= 1) { clearInterval(pwTimerRef.current!); setShowPassword(false); return null; }
          return prev - 1;
        });
      }, 1000);
    }
  }, [showPassword]);

  const toggleConfirm = useCallback(() => {
    if (showConfirm) {
      if (cfTimerRef.current) clearInterval(cfTimerRef.current);
      setShowConfirm(false); setCfCountdown(null);
    } else {
      setShowConfirm(true); setCfCountdown(5);
      cfTimerRef.current = setInterval(() => {
        setCfCountdown(prev => {
          if (prev === null || prev <= 1) { clearInterval(cfTimerRef.current!); setShowConfirm(false); return null; }
          return prev - 1;
        });
      }, 1000);
    }
  }, [showConfirm]);
  const [errors, setErrors] = useState<FormErrors<RegisterField>>({});
  const [touched, setTouched] = useState<TouchedFields<RegisterField>>({
    firstName: false, lastName: false, email: false, password: false, confirmPassword: false,
  });
  const [focused, setFocused] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const isLoading = externalLoading || isSubmitting;
  const strength = calculatePasswordStrength(form.password);

  const handleChange = useCallback((field: RegisterField) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(p => ({ ...p, [field]: e.target.value }));
    if (errors[field]) setErrors(p => ({ ...p, [field]: undefined }));
    if (externalError && onClearError) onClearError();
  }, [errors, externalError, onClearError]);

  const handleBlur = useCallback((field: RegisterField) => {
    setTouched(p => ({ ...p, [field]: true }));
    setFocused(null);
    let error: string | undefined;
    switch (field) {
      case 'firstName': error = validateFirstName(form.firstName); break;
      case 'lastName':  error = validateLastName(form.lastName);   break;
      case 'email':    error = validateEmail(form.email);          break;
      case 'password': error = validatePassword(form.password);    break;
      case 'confirmPassword': error = validateConfirm(form.password, form.confirmPassword); break;
    }
    setErrors(p => ({ ...p, [field]: error }));
  }, [form]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const newErrors: FormErrors<RegisterField> = {
      firstName: validateFirstName(form.firstName),
      lastName: validateLastName(form.lastName),
      email: validateEmail(form.email),
      password: validatePassword(form.password),
      confirmPassword: validateConfirm(form.password, form.confirmPassword),
    };
    setErrors(newErrors);
    setTouched({ firstName: true, lastName: true, email: true, password: true, confirmPassword: true });
    if (Object.values(newErrors).some(v => v !== undefined)) return;
    setIsSubmitting(true);
    try {
      const displayName = `${form.firstName.trim()} ${form.lastName.trim()}`;
      await onSubmit(form.email, form.password, displayName, form.firstName.trim(), form.lastName.trim());
      if (isMounted()) setIsSuccess(true);
    } finally { if (isMounted()) setIsSubmitting(false); }
  };

  const inputStyle = (field: string, hasError: boolean, pw = false): CSSProperties => ({
    width: '100%', padding: '10px 12px 10px 40px', paddingRight: pw ? '44px' : '12px',
    background: t.bgInput,
    border: `1px solid ${hasError ? t.borderError : focused === field ? t.borderFocus : t.border}`,
    borderRadius: '8px', color: t.text, fontSize: '14px',
    fontFamily: field === 'email' ? "'JetBrains Mono', monospace" : "'Space Grotesk', sans-serif",
    outline: 'none',
    boxShadow: focused === field ? `0 0 0 3px ${hasError ? t.errorBg : t.focusRing}` : 'none',
    transition: 'border-color 0.15s, box-shadow 0.15s',
  });
  const iconStyle: CSSProperties = {
    position: 'absolute', left: '12px', top: '50%',
    transform: 'translateY(-50%)', color: t.textMuted, pointerEvents: 'none',
  };
  const labelStyle: CSSProperties = {
    display: 'block', marginBottom: '6px', fontSize: '14px',
    fontWeight: 500, color: t.textSecondary, fontFamily: "'Space Grotesk', sans-serif",
  };
  const errStyle: CSSProperties = {
    display: 'flex', alignItems: 'center', gap: '4px',
    marginTop: '4px', fontSize: '12px', color: t.error,
  };
  const toggleStyle: CSSProperties = {
    position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)',
    background: 'none', border: 'none', padding: '4px', cursor: 'pointer',
    color: t.textMuted, display: 'flex',
  };

  const successContent = (
    <>
      <div style={{
        width: '64px', height: '64px', background: t.successBg, borderRadius: '50%',
        display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px',
      }}>
        <CheckCircle2 size={32} style={{ color: t.success }} />
      </div>
      <h2 style={cardStyles.title}>¡Registro completado!</h2>
      <p style={{ fontSize: '14px', color: t.textSecondary, textAlign: 'center', marginBottom: '4px' }}>
        Bienvenido/a, <strong style={{ color: t.text }}>{form.firstName}</strong>
      </p>
      <p style={{ fontSize: '14px', color: t.textSecondary, textAlign: 'center', marginBottom: '8px' }}>Hemos enviado un email de verificación a</p>
      <p style={{ fontSize: '14px', color: t.text, fontWeight: 600, textAlign: 'center', marginBottom: '16px', fontFamily: "'JetBrains Mono', monospace" }}>{form.email}</p>
      <p style={{ fontSize: '13px', color: t.textMuted, textAlign: 'center', marginBottom: '24px' }}>Por favor, verifica tu correo antes de iniciar sesión. Revisa también la carpeta de spam.</p>
      {onNavigateToLogin && <button onClick={onNavigateToLogin} style={buttonStyles.base}>Ir a iniciar sesión</button>}
    </>
  );

  const formContent = isSuccess ? successContent : (
    <>
      {!hideTitle && (
        <>
          <h2 style={cardStyles.title}>Crear cuenta</h2>
          <p style={cardStyles.subtitle}>Regístrate para gestionar documentos PTEL de forma ágil y eficiente</p>
        </>
      )}
      {externalError && (
        <div style={alertStyles.container}>
          <AlertCircle size={18} style={alertStyles.icon} />
          <span style={alertStyles.text}>{externalError}</span>
        </div>
      )}
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <div>
            <label htmlFor="register-firstName" style={labelStyle}>Nombre</label>
            <div style={{ position: 'relative' }}>
              <User size={18} style={iconStyle} aria-hidden="true" />
              <input id="register-firstName" type="text" value={form.firstName}
                onChange={handleChange('firstName')} onFocus={() => setFocused('firstName')}
                onBlur={() => handleBlur('firstName')} placeholder="Juan"
                disabled={isLoading} autoComplete="given-name"
                style={inputStyle('firstName', !!(touched.firstName && errors.firstName))}
              />
            </div>
            {touched.firstName && errors.firstName && <div style={errStyle}><AlertCircle size={12} /><span>{errors.firstName}</span></div>}
          </div>
          <div>
            <label htmlFor="register-lastName" style={labelStyle}>Apellidos</label>
            <div style={{ position: 'relative' }}>
              <User size={18} style={iconStyle} aria-hidden="true" />
              <input id="register-lastName" type="text" value={form.lastName}
                onChange={handleChange('lastName')} onFocus={() => setFocused('lastName')}
                onBlur={() => handleBlur('lastName')} placeholder="García López"
                disabled={isLoading} autoComplete="family-name"
                style={inputStyle('lastName', !!(touched.lastName && errors.lastName))}
              />
            </div>
            {touched.lastName && errors.lastName && <div style={errStyle}><AlertCircle size={12} /><span>{errors.lastName}</span></div>}
          </div>
        </div>
        <div>
          <label htmlFor="register-email" style={labelStyle}>Correo electrónico</label>
          <div style={{ position: 'relative' }}>
            <Mail size={18} style={iconStyle} aria-hidden="true" />
            <input id="register-email" type="email" value={form.email}
              onChange={handleChange('email')} onFocus={() => setFocused('email')}
              onBlur={() => handleBlur('email')} placeholder="tu@email.com"
              disabled={isLoading} autoComplete="email"
              style={inputStyle('email', !!(touched.email && errors.email))}
            />
          </div>
          {touched.email && errors.email && <div style={errStyle}><AlertCircle size={12} /><span>{errors.email}</span></div>}
        </div>
        <div>
          <label htmlFor="register-password" style={labelStyle}>Contraseña</label>
          <div style={{ position: 'relative' }}>
            <Lock size={18} style={iconStyle} aria-hidden="true" />
            <input id="register-password" type={showPassword ? 'text' : 'password'}
              value={form.password} onChange={handleChange('password')}
              onFocus={() => setFocused('password')} onBlur={() => handleBlur('password')}
              placeholder="••••••••" disabled={isLoading} autoComplete="new-password"
              style={inputStyle('password', !!(touched.password && errors.password), true)}
            />
            <button type="button" onClick={togglePassword} style={toggleStyle}
              tabIndex={-1} aria-label={showPassword ? 'Ocultar' : 'Mostrar'}>
              {showPassword ? <EyeOff size={16} /> : <Eye size={18} />}
              {pwCountdown !== null && (
                <span style={{ fontSize: '10px', fontFamily: "'JetBrains Mono', monospace", lineHeight: 1, minWidth: '8px' }}>
                  {pwCountdown}
                </span>
              )}
            </button>
          </div>
          {touched.password && errors.password && <div style={errStyle}><AlertCircle size={12} /><span>{errors.password}</span></div>}
          {form.password && (
            <>
              <div style={{ display: 'flex', gap: '4px', marginTop: '8px', height: '4px' }}>
                {[1,2,3,4].map(level => (
                  <div key={level} style={{
                    flex: 1, borderRadius: '2px',
                    background: level <= strength ? getStrengthColor(strength, theme) : t.border,
                    transition: 'background 0.2s',
                  }} />
                ))}
              </div>
              <p style={{ marginTop: '4px', fontSize: '12px', color: t.textMuted }}>
                Fortaleza: <span style={{ color: strength >= 3 ? t.success : t.textSecondary }}>{getStrengthLabel(strength)}</span>
              </p>
            </>
          )}
        </div>
        <div>
          <label htmlFor="register-confirmPassword" style={labelStyle}>Confirmar contraseña</label>
          <div style={{ position: 'relative' }}>
            <Lock size={18} style={iconStyle} aria-hidden="true" />
            <input id="register-confirmPassword" type={showConfirm ? 'text' : 'password'}
              value={form.confirmPassword} onChange={handleChange('confirmPassword')}
              onFocus={() => setFocused('confirmPassword')} onBlur={() => handleBlur('confirmPassword')}
              placeholder="••••••••" disabled={isLoading} autoComplete="new-password"
              style={inputStyle('confirmPassword', !!(touched.confirmPassword && errors.confirmPassword), true)}
            />
            <button type="button" onClick={toggleConfirm} style={toggleStyle}
              tabIndex={-1} aria-label={showConfirm ? 'Ocultar' : 'Mostrar'}>
              {showConfirm ? <EyeOff size={16} /> : <Eye size={18} />}
              {cfCountdown !== null && (
                <span style={{ fontSize: '10px', fontFamily: "'JetBrains Mono', monospace", lineHeight: 1, minWidth: '8px' }}>
                  {cfCountdown}
                </span>
              )}
            </button>
          </div>
          {touched.confirmPassword && errors.confirmPassword && <div style={errStyle}><AlertCircle size={12} /><span>{errors.confirmPassword}</span></div>}
        </div>
        <button type="submit" disabled={isLoading}
          style={{ ...buttonStyles.base, marginTop: '8px', ...(isLoading ? buttonStyles.disabled : {}) }}>
          {isLoading
            ? <><Loader2 size={18} className="animate-spin" /><span>Creando cuenta...</span></>
            : <span>Crear cuenta</span>}
        </button>
      </form>
      {!hideTitle && onNavigateToLogin && (
        <>
          <div style={cardStyles.divider}>
            <div style={cardStyles.dividerLine} /><span style={cardStyles.dividerText}>o</span><div style={cardStyles.dividerLine} />
          </div>
          <p style={{ ...cardStyles.footer, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '24px' }}>
            <span>¿Ya tienes cuenta?</span>
            <button type="button" onClick={onNavigateToLogin} disabled={isLoading} style={cardStyles.link}>Iniciar sesión</button>
          </p>
        </>
      )}
    </>
  );

  if (hideCard) return <>{formContent}<style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}.animate-spin{animation:spin 1s linear infinite}`}</style></>;

  return (
    <div style={{ width: '100%', maxWidth: '440px', margin: '0 auto' }}>
      <div style={{ ...cardStyles.container, position: 'relative' }}>
        {onClose && (
          <button type="button" onClick={onClose} aria-label="Cerrar" style={{
            position: 'absolute', top: '12px', right: '12px', zIndex: 10,
            width: '32px', height: '32px', background: 'transparent',
            border: `1px solid ${t.border}`, borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: t.textSecondary, cursor: 'pointer', padding: 0,
          }}><span style={{ fontSize: '16px', lineHeight: 1 }}>×</span></button>
        )}
        {formContent}
      </div>
      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}.animate-spin{animation:spin 1s linear infinite}`}</style>
    </div>
  );
}

export default RegisterForm;
